import React, { useState } from 'react';
import { NavLink, useHistory, useLocation } from 'react-router-dom';
import {
    activitiesPath,
    clearCrmSessionToken,
    contactsPath,
    crmBasePath,
    dashboardPath,
    dealsPath,
    getCrmSessionActor,
    isCrmPortalMode,
    leadsPath,
    leadSourcesPath,
    teamPath,
    automationsPath,
} from '../utils/crmEnv';

// Sidebar styles
const sidebarStyles = {
    container: {
        width: '240px',
        height: '100%',
        backgroundColor: 'var(--crm-sidebar-bg)',
        borderRight: '1px solid var(--crm-gray-200)',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
    },
    header: {
        padding: '22px 16px 18px',
        borderBottom: '1px solid var(--crm-gray-800)',
    },
    headerBrandRow: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: '12px',
    },
    headerLogo: {
        width: '180px',
        maxWidth: '100%',
        height: 'auto',
        maxHeight: '64px',
        objectFit: 'contain',
        display: 'block',
        margin: '0 auto',
    },
    headerTextBlock: {
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        padding: '0 2px',
    },
    headerTitle: {
        fontSize: '20px',
        fontWeight: '800',
        color: '#ffffff',
        margin: 0,
        lineHeight: 1.05,
        letterSpacing: '-0.2px',
    },
    tagline: {
        fontSize: '12px',
        color: 'rgba(255, 255, 255, 0.62)',
        fontWeight: '500',
        lineHeight: 1.35,
    },
    nav: {
        padding: '24px 12px',
        flex: 1,
        overflowY: 'auto',
        minHeight: 0,
        scrollbarGutter: 'stable',
    },
    navLink: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 16px',
        margin: '4px 0',
        borderRadius: 'var(--crm-border-radius-sm)',
        fontSize: '14px',
        fontWeight: '500',
        color: 'rgba(255, 255, 255, 0.6)',
        textDecoration: 'none',
        transition: 'all 0.15s ease',
    },
    navLinkActive: {
        color: '#ffffff',
        backgroundColor: 'var(--crm-sidebar-hover)',
        fontWeight: '600',
        borderLeft: '2px solid #ffffff',
    },
    navLinkHover: {
        backgroundColor: 'var(--crm-sidebar-hover)',
    },
};

function CRMSidebar() {
    const location = useLocation();
    const history = useHistory();
    const basePath = crmBasePath();
    const isPortal = isCrmPortalMode();
    const storedPortalRole = String(getCrmSessionActor()?.role || '').toLowerCase();
    const portalRole = storedPortalRole === 'admin' || storedPortalRole === 'manager' || storedPortalRole === 'agent'
        ? storedPortalRole
        : 'agent';
    const isPortalRestricted = isPortal && portalRole !== 'admin';
    const [showManual, setShowManual] = useState(false);

    const navItems = [
        {
            key: 'dashboard',
            path: dashboardPath(),
            label: 'Dashboard',
            icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="7" height="7" />
                    <rect x="14" y="3" width="7" height="7" />
                    <rect x="14" y="14" width="7" height="7" />
                    <rect x="3" y="14" width="7" height="7" />
                </svg>
            ),
            end: true,
        },
        {
            key: 'leads',
            path: leadsPath(),
            label: 'Leads',
            icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
            ),
        },
        {
            key: 'contacts',
            path: contactsPath(),
            label: 'Contacts',
            icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                </svg>
            ),
        },
        {
            key: 'deals',
            path: dealsPath(),
            label: 'Deals',
            icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="1" x2="12" y2="23" />
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                </svg>
            ),
        },
        {
            key: 'properties',
            path: `${basePath}/properties`,
            label: 'Properties',
            icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 21h18" />
                    <path d="M5 21V7l7-4 7 4v14" />
                    <path d="M9 21v-6h6v6" />
                </svg>
            ),
        },
        {
            key: 'duplicates',
            path: `${basePath}/duplicates`,
            label: 'Duplicates',
            icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="8" y="8" width="12" height="12" rx="2" ry="2" />
                    <rect x="4" y="4" width="12" height="12" rx="2" ry="2" />
                </svg>
            ),
        },
        {
            key: 'activities',
            path: activitiesPath(),
            label: 'Activities',
            icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                    <line x1="8" y1="14" x2="8" y2="14" />
                    <line x1="12" y1="14" x2="12" y2="14" />
                    <line x1="16" y1="14" x2="16" y2="14" />
                    <line x1="8" y1="18" x2="8" y2="18" />
                    <line x1="12" y1="18" x2="12" y2="18" />
                </svg>
            ),
        },
        {
            key: 'audit',
            path: `${basePath}/audit`,
            label: 'Audit Log',
            icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10 9 9 9 8 9" />
                </svg>
            ),
        },
        {
            key: 'automations',
            path: automationsPath(),
            label: 'Automations',
            icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="3" />
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.03A1.65 1.65 0 0 0 10 3.09V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h.03a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.03A1.65 1.65 0 0 0 20.91 10H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
            ),
        },
        !isPortal && {
            key: 'lead_sources',
            path: leadSourcesPath(),
            label: 'Lead Sources',
            icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 7h16" />
                    <path d="M4 12h16" />
                    <path d="M4 17h10" />
                    <circle cx="18" cy="17" r="2" />
                </svg>
            ),
        },
        !isPortal && {
            key: 'team',
            path: teamPath(),
            label: 'Team',
            icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
            ),
        },
        {
            key: 'manual',
            path: '#manual',
            label: 'Manual',
            icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16v16H4z" />
                    <path d="M8 4v16" />
                    <path d="M12 8h6" />
                    <path d="M12 12h6" />
                    <path d="M12 16h6" />
                </svg>
            ),
            manual: true,
        },
    ]
        .filter(Boolean)
        .filter((item) => {
            if (!isPortalRestricted) return true;
            if (item.manual) return true;
            const allowedForAgent = new Set(['dashboard', 'leads', 'contacts', 'deals', 'properties', 'activities']);
            const allowedForManager = new Set(['dashboard', 'leads', 'contacts', 'deals', 'properties', 'activities', 'audit']);
            const allowed = portalRole === 'manager' ? allowedForManager : allowedForAgent;
            return allowed.has(item.key);
        });

    const isActive = (path, end) => {
        if (end) {
            return location.pathname === path;
        }
        return location.pathname.startsWith(path);
    };

    return (
        <aside className="crm-sidebar-shell" style={sidebarStyles.container}>
            {/* Header */}
            <div style={sidebarStyles.header}>
                <div style={sidebarStyles.headerBrandRow}>
                    <img
                        src="/Douglas2-removebg-preview.png"
                        alt="Douglas & McCartney"
                        style={sidebarStyles.headerLogo}
                    />
                    <div style={sidebarStyles.headerTextBlock}>
                        <h2 style={sidebarStyles.headerTitle}>CRM</h2>
                        <div style={sidebarStyles.tagline}>Manage Your Sales</div>
                    </div>
                </div>
            </div>

            {/* Navigation */}
            <nav className="crm-sidebar-nav-scroll" style={sidebarStyles.nav}>
                {navItems.map((item) => {
                    if (item.manual) {
                        return (
                            <button
                                key={item.path}
                                type="button"
                                className="crm-sidebar-btn"
                                onClick={() => setShowManual(true)}
                            >
                                {item.icon}
                                <span>{item.label}</span>
                            </button>
                        );
                    }

                    return (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            exact={item.end}
                            style={{
                                ...sidebarStyles.navLink,
                                ...(isActive(item.path, item.end) ? sidebarStyles.navLinkActive : {}),
                            }}
                            onMouseEnter={(e) => {
                                if (!isActive(item.path, item.end)) {
                                    e.currentTarget.style.backgroundColor = 'var(--crm-sidebar-hover)';
                                    e.currentTarget.style.color = '#ffffff';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!isActive(item.path, item.end)) {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = 'rgba(255, 255, 255, 0.6)';
                                }
                            }}
                        >
                            {item.icon}
                            <span>{item.label}</span>
                        </NavLink>
                    );
                })}
            </nav>

            {isPortal && (
                <div style={{ padding: '12px' }}>
                    <button
                        type="button"
                        className="crm-sidebar-btn"
                        onClick={() => {
                            clearCrmSessionToken();
                            history.push('/crm-portal/login');
                        }}
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                            <polyline points="16 17 21 12 16 7" />
                            <line x1="21" y1="12" x2="9" y2="12" />
                        </svg>
                        <span>Logout</span>
                    </button>
                </div>
            )}

            {showManual && (
                <div className="crm-manual-overlay" onClick={() => setShowManual(false)}>
                    <div className="crm-manual-panel" onClick={(event) => event.stopPropagation()}>
                        <div className="crm-manual-header">
                            <h3>CRM Hotkeys Manual</h3>
                            <button className="crm-modal-close" onClick={() => setShowManual(false)}>X</button>
                        </div>
                        <div className="crm-manual-section">
                            <h4>Global</h4>
                            <div className="crm-manual-row"><span>Open Command Palette</span><kbd>Ctrl/⌘ + K</kbd></div>
                            <div className="crm-manual-row"><span>Close overlays</span><kbd>Esc</kbd></div>
                            <div className="crm-manual-row"><span>Focus search</span><kbd>/</kbd></div>
                        </div>
                        <div className="crm-manual-section">
                            <h4>Quick Create</h4>
                            <div className="crm-manual-row"><span>Create Lead</span><kbd>N</kbd> then <kbd>L</kbd></div>
                            <div className="crm-manual-row"><span>Create Contact</span><kbd>N</kbd> then <kbd>C</kbd></div>
                            <div className="crm-manual-row"><span>Create Deal</span><kbd>N</kbd> then <kbd>D</kbd></div>
                            <div className="crm-manual-row"><span>Create Activity</span><kbd>N</kbd> then <kbd>A</kbd></div>
                        </div>
                        <div className="crm-manual-section">
                            <h4>Navigation</h4>
                            <div className="crm-manual-row"><span>Go Dashboard</span><kbd>G</kbd> then <kbd>D</kbd></div>
                            <div className="crm-manual-row"><span>Go Leads</span><kbd>G</kbd> then <kbd>L</kbd></div>
                            <div className="crm-manual-row"><span>Go Contacts</span><kbd>G</kbd> then <kbd>C</kbd></div>
                            <div className="crm-manual-row"><span>Go Deals</span><kbd>G</kbd> then <kbd>E</kbd></div>
                            <div className="crm-manual-row"><span>Go Activities</span><kbd>G</kbd> then <kbd>A</kbd></div>
                        </div>
                    </div>
                </div>
            )}
        </aside>
    );
}

export default CRMSidebar;
